import { TOKEN_ADDRESSES } from '../swapconfig/config';
import CakeV3PoolAbi from '../abi/CakeV3PoolAbi.json';
import ERC20_ABI from '../abi/Erc20Abi.json';
import { fetchV3PoolData } from './v3manager.js';
import cachedWeb3Utils from '../utils/cachedWeb3Utils.js';
import { Web3 } from 'web3';

const FALLBACK_RES = {
  token0Balance: '0',
  token1Balance: '0',
  token0Price: 0,
  token1Price: 0,
  totalLiquidityUSD: 0,
  currentPrice: 0,
  effectiveLiquidityUSD: 0,
}

// Uniswap/PancakeSwap V3 Pool ABI
const POOL_ABI = CakeV3PoolAbi;

// 调用锁管理 - 确保同一时间只有一个fetchLiquidityInfo在执行
const activeLiquidityFetches = new Set();

// 缓存相关配置
const CACHE_KEY_PREFIX = 'liquidity_cache_';
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5分钟过期

// 缓存工具函数
const getCacheKey = (poolAddress) => `${CACHE_KEY_PREFIX}${poolAddress.toLowerCase()}`;

const readFromCache = (poolAddress) => {
  try {
    const cacheKey = getCacheKey(poolAddress);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // 检查是否过期
    if (now - timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    console.log(`从缓存读取流动性数据: ${poolAddress}`);
    return { ...data, fromCache: true };
  } catch (error) {
    console.error('读取缓存失败:', error);
    return null;
  }
};

const writeToCache = (poolAddress, data) => {
  try {
    const cacheKey = getCacheKey(poolAddress);
    const cacheData = {
      data: { ...data, fromCache: false }, // 确保缓存的数据不带fromCache标记
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`流动性数据已缓存: ${poolAddress}`);
  } catch (error) {
    console.error('写入缓存失败:', error);
  }
};

// 清理过期缓存
const cleanExpiredCache = () => {
  try {
    const now = Date.now();
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (now - cached.timestamp > CACHE_EXPIRY_MS) {
            keysToRemove.push(key);
          }
        } catch (e) {
          keysToRemove.push(key); // 损坏的缓存也删除
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log(`清理了 ${keysToRemove.length} 个过期缓存`);
    }
  } catch (error) {
    console.error('清理缓存失败:', error);
  }
};

// 初始化时清理过期缓存
cleanExpiredCache();

// 价格缓存相关配置
const PRICE_CACHE_KEY_PREFIX = 'token_price_cache_';
const PRICE_CACHE_EXPIRY_MS = 60 * 60 * 1000; // 1小时过期

// 默认价格配置
const DEFAULT_PRICES = {
  [TOKEN_ADDRESSES.USDT.toLowerCase()]: 1, // USDT固定为1
  [TOKEN_ADDRESSES.USDC.toLowerCase()]: 1, // USDC固定为1
  [TOKEN_ADDRESSES.WBNB.toLowerCase()]: 670, // WBNB默认价格
  '0xe9e7cea3dedca5984780bafc599bd69add087d56': 1, // BUSD固定为1
};

// 价格缓存工具函数
const getPriceCacheKey = (tokenAddress) => `${PRICE_CACHE_KEY_PREFIX}${tokenAddress.toLowerCase()}`;

const readPriceFromCache = (tokenAddress) => {
  try {
    const cacheKey = getPriceCacheKey(tokenAddress);
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const { price, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // 检查是否过期
    if (now - timestamp > PRICE_CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    console.log(`从缓存读取token价格: ${tokenAddress} = $${price}`);
    return price;
  } catch (error) {
    console.error('读取价格缓存失败:', error);
    return null;
  }
};

const writePriceToCache = (tokenAddress, price) => {
  try {
    const cacheKey = getPriceCacheKey(tokenAddress);
    const cacheData = {
      price,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(`token价格已缓存: ${tokenAddress} = $${price}`);
  } catch (error) {
    console.error('写入价格缓存失败:', error);
  }
};

// 清理过期价格缓存
const cleanExpiredPriceCache = () => {
  try {
    const now = Date.now();
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(PRICE_CACHE_KEY_PREFIX)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (now - cached.timestamp > PRICE_CACHE_EXPIRY_MS) {
            keysToRemove.push(key);
          }
        } catch (e) {
          keysToRemove.push(key); // 损坏的缓存也删除
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log(`清理了 ${keysToRemove.length} 个过期价格缓存`);
    }
  } catch (error) {
    console.error('清理价格缓存失败:', error);
  }
};

// 初始化时清理过期价格缓存
cleanExpiredPriceCache();

// Function to get token price from CoinGecko API with caching and fallbacks
export const getTokenPrice = async (tokenAddress) => {
  if (!tokenAddress) return 0;

  const tokenAddressLower = tokenAddress.toLowerCase();

  // 先检查缓存
  const cachedPrice = readPriceFromCache(tokenAddress);
  if (cachedPrice !== null) {
    return cachedPrice;
  }

  // 检查是否是固定价格的稳定币（USDT、USDC、BUSD直接返回1，不需要API请求）
  if (tokenAddressLower === TOKEN_ADDRESSES.USDT.toLowerCase() ||
    tokenAddressLower === TOKEN_ADDRESSES.USDC.toLowerCase() ||
    tokenAddressLower === '0xe9e7cea3dedca5984780bafc599bd69add087d56') { // BUSD
    const price = 1;
    writePriceToCache(tokenAddress, price);
    return price;
  }

  try {
    // Common token addresses for BSC
    const commonTokens = {
      [TOKEN_ADDRESSES.USDT.toLowerCase()]: 'tether', // USDT
      [TOKEN_ADDRESSES.USDC.toLowerCase()]: 'usd-coin', // USDC
      [TOKEN_ADDRESSES.WBNB.toLowerCase()]: 'wbnb', // WBNB
      '0xe9e7cea3dedca5984780bafc599bd69add087d56': 'binance-usd', // BUSD
    };

    const tokenId = commonTokens[tokenAddressLower];
    if (!tokenId) {
      // For unknown tokens, return default price or 0
      const defaultPrice = DEFAULT_PRICES[tokenAddressLower] || 0;
      if (defaultPrice > 0) {
        writePriceToCache(tokenAddress, defaultPrice);
      }
      return defaultPrice;
    }

    const response = await fetch(
      `https://api.coingecko.com/api/v3/simple/price?ids=${tokenId}&vs_currencies=usd`,
      {
        timeout: 5000, // 5秒超时
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const price = data[tokenId]?.usd || 0;

    if (price > 0) {
      // 缓存成功获取的价格
      writePriceToCache(tokenAddress, price);
      console.log(`成功获取token价格: ${tokenAddress} = $${price}`);
      return price;
    } else {
      throw new Error('API返回的价格为0或无效');
    }

  } catch (error) {
    console.error(`获取token价格失败 (${tokenAddress}):`, error.message);

    // 使用默认价格作为兜底
    const fallbackPrice = DEFAULT_PRICES[tokenAddressLower] || 0;
    if (fallbackPrice > 0) {
      console.log(`使用默认价格兜底: ${tokenAddress} = $${fallbackPrice}`);
      // 缓存兜底价格，但设置较短的过期时间（10分钟）
      try {
        const cacheKey = getPriceCacheKey(tokenAddress);
        const cacheData = {
          price: fallbackPrice,
          timestamp: Date.now(),
          isFallback: true
        };
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (e) {
        console.error('缓存兜底价格失败:', e);
      }
      return fallbackPrice;
    }

    return 0;
  }
};

// V3 tick and liquidity calculation functions - 使用原生BigInt高精度计算

export const tickToPrice = (tick, token0Decimals, token1Decimals) => {
  /**
   * Convert a tick to a price using high precision BigInt
   * Price = 1.0001^tick * (10^(token1Decimals - token0Decimals))
   */
  try {
    const tickBigInt = BigInt(tick);

    // 计算 1.0001^tick
    // 1.0001 = 10001/10000，用高精度表示
    const base = bigIntDiv(BigInt(10001) * PRECISION_SCALE, BigInt(10000) * PRECISION_SCALE, PRECISION_SCALE);

    let price;
    if (tick >= 0) {
      price = bigIntPowAdvanced(base, tickBigInt, PRECISION_SCALE);
    } else {
      // 负指数：1.0001^(-tick) = 1 / 1.0001^tick
      const positivePower = bigIntPowAdvanced(base, -tickBigInt, PRECISION_SCALE);
      price = bigIntDiv(PRECISION_SCALE, positivePower, PRECISION_SCALE);
    }

    // 计算小数点调整 10^(token1Decimals - token0Decimals)
    const decimalDiff = Number(token1Decimals) - Number(token0Decimals);
    if (decimalDiff !== 0) {
      const decimalAdjustment = BigInt(10 ** Math.abs(decimalDiff));
      if (decimalDiff > 0) {
        price = bigIntMul(price, decimalAdjustment, PRECISION_SCALE);
      } else {
        price = bigIntDiv(price, decimalAdjustment, PRECISION_SCALE);
      }
    }

    // 转换回普通数字（保持足够精度）
    return Number(price) / Number(PRECISION_SCALE);
  } catch (error) {
    console.error('tickToPrice error:', error);
    // 回退到低精度计算
    const price = Math.pow(1.0001, tick);
    const decimalAdjustment = Math.pow(10, Number(token1Decimals) - Number(token0Decimals));
    return price * decimalAdjustment;
  }
};

export const priceToTickBigInt = (priceBigInt, token0Decimals, token1Decimals) => {
  /**
   * Convert a high precision price (BigInt) to its nearest tick using Math methods for consistency
   * Based on: tick = floor(log(price) / log(1.0001))
   * 
   * @param {BigInt} priceBigInt - Price in high precision (scaled by PRECISION_SCALE)
   * @param {number|BigInt} token0Decimals - Token0 decimals
   * @param {number|BigInt} token1Decimals - Token1 decimals
   * @returns {number} - Tick index
   */
  try {
    // 转换 BigInt 价格为普通数字
    const priceFloat = Number(priceBigInt) / Number(PRECISION_SCALE);

    // 计算小数点调整 - 这需要做与tickToPrice相反的操作
    const decimalDiff = Number(token1Decimals) - Number(token0Decimals);
    const decimalAdjustment = Math.pow(10, decimalDiff);

    // 调整价格以抵消token decimals的影响
    const adjustedPrice = priceFloat / decimalAdjustment;

    // 确保调整后的价格为正数
    if (adjustedPrice <= 0) {
      console.error('调整后的价格必须为正数:', adjustedPrice);
      return 0;
    }

    // 使用正确的Uniswap V3公式: tick = log(adjustedPrice) / log(1.0001)
    const tick = Math.log(adjustedPrice) / Math.log(1.0001);

    // 向下取整 (floor) - 根据Uniswap文档
    const finalTick = Math.floor(tick);

    return finalTick;
  } catch (error) {
    console.error('priceToTickBigInt error:', error);
    console.error('输入参数:', { priceBigInt: priceBigInt.toString(), token0Decimals, token1Decimals });
    return 0;
  }
};

export const priceToTick = (price, token0Decimals, token1Decimals) => {
  /**
   * Convert a price to its nearest tick - wrapper for high precision version
   * @param {number} price - Price as a regular number
   * @param {number} token0Decimals - Token0 decimals  
   * @param {number} token1Decimals - Token1 decimals
   * @returns {number} - Tick index
   */
  try {
    // 转换普通数字价格到高精度BigInt
    const priceBigInt = BigInt(Math.floor(price * Number(PRECISION_SCALE)));

    // 调用高精度版本
    return priceToTickBigInt(priceBigInt, token0Decimals, token1Decimals);
  } catch (error) {
    console.error('priceToTick wrapper error:', error);
    // 回退到低精度计算
    const decimalAdjustment = Math.pow(10, Number(token1Decimals) - Number(token0Decimals));
    const adjustedPrice = price / decimalAdjustment;
    return Math.round(Math.log(adjustedPrice) / Math.log(1.0001));
  }
};
const unlimitWeb3=new Web3('https://bsc-dataseed3.binance.org');

// 内部实际执行流动性获取的函数（不带缓存，仅供fetchLiquidityInfo调用）
const _fetchLiquidityInfoInternal = async (web3, poolAddress) => {
  // First, validate that this is a valid V3 pool by checking basic properties
  const poolContract = new unlimitWeb3.eth.Contract(POOL_ABI, poolAddress);

  let poolToken0, poolToken1;
  [poolToken0, poolToken1] = await Promise.all([
    cachedWeb3Utils.getCachedPoolToken0(poolAddress),
    cachedWeb3Utils.getCachedPoolToken1(poolAddress),
  ]);


  // Get token contracts
  const token0Contract = new web3.eth.Contract(ERC20_ABI, poolToken0);
  const token1Contract = new web3.eth.Contract(ERC20_ABI, poolToken1);

  // Get pool balances
  const [token0Balance, token1Balance] = await Promise.all([
    token0Contract.methods.balanceOf(poolAddress).call(),
    token1Contract.methods.balanceOf(poolAddress).call()
  ]);

  const [token0Pricex, token1Pricez] = await Promise.all([
    getTokenPrice(poolToken0),
    getTokenPrice(poolToken1)
  ]);
  let token0Price=token0Pricex;
  let token1Price=token1Pricez;
  if(token0Price==0 && token1Price==0){
    token0Price=1;
  }
  // Convert token balances to Ether units
  const token0BalanceEther = web3.utils.fromWei(token0Balance, 'ether');
  const token1BalanceEther = web3.utils.fromWei(token1Balance, 'ether');

  // Get slot0 data to access sqrtPriceX96
  const slot0Data = await poolContract.methods.slot0().call();
  const sqrtPriceX96 = BigInt(slot0Data.sqrtPriceX96 || slot0Data[0]);

  // Calculate price from sqrtPriceX96
  const sqrtPrice = Number(sqrtPriceX96) / Number(BigInt(2) ** BigInt(96));
  const rawPrice = sqrtPrice * sqrtPrice;

  // Get token decimals
  const [token0Decimals, token1Decimals] = await Promise.all([
    cachedWeb3Utils.getTokenDecimals(poolToken0),
    cachedWeb3Utils.getTokenDecimals(poolToken1)
  ]);

  // Adjust for decimals difference
  const decimalAdjustment = Math.pow(10, Number(token0Decimals) - Number(token1Decimals));
  const poolExchangeRate = rawPrice * decimalAdjustment;

  // Calculate prices when one is missing
  let finalToken0Price = token0Price;
  let finalToken1Price = token1Price;

  if ((token0Price === 0 || token0Price === null) && token1Price > 0) {
    // token0 price is missing, calculate from token1 price and pool rate
    finalToken0Price = token1Price * poolExchangeRate;
    console.log(`Token0 price missing, calculated: $${finalToken0Price.toFixed(6)} (from Token1: $${token1Price}, rate: ${poolExchangeRate.toFixed(8)})`);
  } else if ((token1Price === 0 || token1Price === null) && token0Price > 0) {
    // token1 price is missing, calculate from token0 price and pool rate
    finalToken1Price = token0Price / poolExchangeRate;
    console.log(`Token1 price missing, calculated: $${finalToken1Price.toFixed(6)} (from Token0: $${token0Price}, rate: ${poolExchangeRate.toFixed(8)})`);
  }

  const token0ValueUSD = parseFloat(token0BalanceEther) * finalToken0Price;
  const token1ValueUSD = parseFloat(token1BalanceEther) * finalToken1Price;
  const totalLiquidityUSD = token0ValueUSD + token1ValueUSD;

  // Fetch liquidity within 0.1% price range using v3manager
  let effectiveLiquidityUSD = 0; // Default fallback
  
  // Calculate the correct display price in USD
  let displayPrice;
  
  // 检查哪个是稳定币或BNB
  const isToken0Stable = 
    poolToken0.toLowerCase() === TOKEN_ADDRESSES.USDT.toLowerCase() || 
    poolToken0.toLowerCase() === TOKEN_ADDRESSES.USDC.toLowerCase() || 
    poolToken0.toLowerCase() === '0xe9e7cea3dedca5984780bafc599bd69add087d56'; // BUSD
  
  const isToken0BNB = poolToken0.toLowerCase() === TOKEN_ADDRESSES.WBNB.toLowerCase();
  
  const isToken1Stable = 
    poolToken1.toLowerCase() === TOKEN_ADDRESSES.USDT.toLowerCase() || 
    poolToken1.toLowerCase() === TOKEN_ADDRESSES.USDC.toLowerCase() || 
    poolToken1.toLowerCase() === '0xe9e7cea3dedca5984780bafc599bd69add087d56'; // BUSD
  
  const isToken1BNB = poolToken1.toLowerCase() === TOKEN_ADDRESSES.WBNB.toLowerCase();
  
  if (isToken0Stable && !isToken1Stable) {
    // Token0 是稳定币，Token1 是代币，价格 = 1 / poolExchangeRate
    displayPrice = finalToken1Price;
    console.log(`Token1 USD价格 (Token0是稳定币): $${displayPrice.toFixed(6)}`);
  } else if (isToken1Stable && !isToken0Stable) {
    // Token1 是稳定币，Token0 是代币，价格 = poolExchangeRate
    displayPrice = finalToken0Price;
    console.log(`Token0 USD价格 (Token1是稳定币): $${displayPrice.toFixed(6)}`);
  } else if (isToken0BNB && !isToken1Stable) {
    // Token0 是BNB，Token1 是代币，价格 = BNB价格 / poolExchangeRate
    displayPrice = finalToken1Price;
    console.log(`Token1 USD价格 (Token0是BNB): $${displayPrice.toFixed(6)}`);
  } else if (isToken1BNB && !isToken0Stable) {
    // Token1 是BNB，Token0 是代币，价格 = poolExchangeRate * BNB价格
    displayPrice = finalToken0Price;
    console.log(`Token0 USD价格 (Token1是BNB): $${displayPrice.toFixed(6)}`);
  } else {
    // 默认情况下使用汇率，但记录警告
    displayPrice = poolExchangeRate;
    console.log(`警告: 无法确定哪个是稳定币/BNB，使用原始汇率: ${poolExchangeRate.toFixed(8)}`);
  }

  // Fetch V3 pool data with 0.1% price delta
  const poolData = await fetchV3PoolData(poolContract, 0.1);

  if (poolData) {
    // Calculate effective liquidity based on token costs and prices
    const token0CostUSD = poolData.token0cost * finalToken0Price;
    const token1CostUSD = poolData.token1cost * finalToken1Price;

    // Total effective liquidity is the sum of both token costs in USD
    effectiveLiquidityUSD = token0CostUSD + token1CostUSD;

    console.log(`Effective liquidity (0.1% range): $${effectiveLiquidityUSD.toFixed(2)}`);
    
    // 应用同样的价格逻辑调整liquidssegments中的价格
    if (poolData.liquidssegments && Array.isArray(poolData.liquidssegments)) {
      // 根据token类型调整每个segment的价格
      poolData.liquidssegments = poolData.liquidssegments.map(segment => {
        let correctedStartPrice = segment.startPrice;
        let correctedEndPrice = segment.endPrice;
        let totalValueUsd = 0;
        
        if(isToken0Stable || isToken0BNB){
          correctedStartPrice = 1/segment.startPrice * finalToken0Price;
          correctedEndPrice = 1/segment.endPrice * finalToken0Price;
        }
        if(isToken1Stable || isToken1BNB){
          correctedStartPrice = segment.startPrice * finalToken1Price;
          correctedEndPrice = segment.endPrice * finalToken1Price;
        }

        // 根据segment中的isToken0标志和当前的token价格进行调整
        if (segment.isToken0) {
          // 计算segment的USD价值 (使用token0消耗量)
          totalValueUsd = segment.tokenConsumption * finalToken0Price;
        } else {
          // 计算segment的USD价值 (使用token1消耗量)
          totalValueUsd = segment.tokenConsumption * finalToken1Price;
        }
        
        return {
          ...segment,
          startPrice: correctedStartPrice,
          endPrice: correctedEndPrice,
          totalValueUsd // 添加USD总价值到segment
        };
      });
    }
  }

  return {
    token0Balance: token0BalanceEther,
    token1Balance: token1BalanceEther,
    token0Price: finalToken0Price,
    token1Price: finalToken1Price,
    totalLiquidityUSD,
    currentPrice: displayPrice,
    effectiveLiquidityUSD,
    poolAddress,
    liquidssegments: poolData?.liquidssegments || []
  };
};

// 带缓存和调用锁的流动性信息获取函数
export const fetchLiquidityInfo = async (web3, poolAddress, useCache = true) => {
  if (!poolAddress) {
    return FALLBACK_RES;
  }


  // 如果启用缓存，先尝试从缓存读取
  if (useCache) {
    const cached = readFromCache(poolAddress);
    if (cached) {
      console.log(`使用缓存的流动性数据: ${poolAddress}`);
      return cached;
    }
  }


  // 如果已有请求正在进行，跳过静默更新
  if (activeLiquidityFetches.has(poolAddress)) {
    return;
  }
  activeLiquidityFetches.add(poolAddress);

  try {
    // 执行实际的流动性获取
    const result = await _fetchLiquidityInfoInternal(web3, poolAddress);

    // 如果获取成功且启用缓存，则缓存结果
    if (useCache && !result.error) {
      writeToCache(poolAddress, result);
    }

    return result;
  } finally {
    activeLiquidityFetches.delete(poolAddress);
  }


};


// Function to fetch token information
export const fetchTokenInfo = async (web3, tokenAddress, tokenType) => {
  if (!tokenAddress || !web3.utils.isAddress(tokenAddress)) return null;

  try {
    const contract = new web3.eth.Contract(ERC20_ABI, tokenAddress);
    const [name, symbol] = await Promise.all([
      cachedWeb3Utils.getTokenName(tokenAddress),
      cachedWeb3Utils.getCachedTokenSymbol(tokenAddress)
    ]);
    return { name, symbol };
  } catch (error) {
    console.error(`Error fetching ${tokenType} info:`, error);
    return null;
  }
};

// Function to fetch pool information
export const fetchPoolInfo = async (web3, poolAddress) => {
  if (!poolAddress || !web3.utils.isAddress(poolAddress)) {
    return {
      token0: '',
      token1: '',
      token0Name: '',
      token1Name: '',
      fee: 0
    };
  }

  try {
    const poolContract = new web3.eth.Contract(POOL_ABI, poolAddress);
    const [token0Address, token1Address, fee] = await Promise.all([
      cachedWeb3Utils.getCachedPoolToken0(poolAddress),
      cachedWeb3Utils.getCachedPoolToken1(poolAddress),
      cachedWeb3Utils.getCachedPoolFee(poolAddress)
    ]);

    // Fetch token names for token0 and token1
    const token0Contract = new web3.eth.Contract(ERC20_ABI, token0Address);
    const token1Contract = new web3.eth.Contract(ERC20_ABI, token1Address);

    const [token0Symbol, token1Symbol] = await Promise.all([
      cachedWeb3Utils.getCachedTokenSymbol(token0Address),
      cachedWeb3Utils.getCachedTokenSymbol(token1Address)
    ]);



    return {
      token0: token0Address,
      token1: token1Address,
      token0Name: token0Symbol,
      token1Name: token1Symbol,
      fee: parseInt(fee)
    };
  } catch (error) {
    console.error('Error fetching pool info:', error);
    return {
      token0: '',
      token1: '',
      token0Name: '',
      token1Name: '',
      fee: 0
    };
  }
};


